import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { Skeleton, Stack } from "@mui/material";
import { doc, updateDoc, writeBatch } from "firebase/firestore";
import { db } from "firebaseApp";
import React from "react";
import { IMAGE_TYPE } from "components/ai-editor/sections/Image";
import { MARKDONW_TYPE } from "components/ai-editor/sections/Markdown";
import { AI_TYPE } from "components/ai-editor/sections/AIBlock";
import { HEADER_TYPE } from "components/ai-editor/sections/Header";
import AddSectionButton from "components/ai-editor/section-controls/AddSectionButton";
import Section from "components/ai-editor/Section";
import ExpandableActionButtons from "components/ai-editor/ExpandableActionButtons";

const INDEX_GAP = 1000; // initial spacing between indexes

export default function DocumentSections({
  documentId,
  loading,
  sections,
  setSections,
  sectionRefs,
}) {
  if (loading) {
    return (
      <Stack spacing={1}>
        <Skeleton variant="rounded" height="64px" />
        <Skeleton variant="rounded" height="128px" />
        <Skeleton variant="rounded" height="128px" />
      </Stack>
    );
  }

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const newSections = Array.from(sections);
    const [movedItem] = newSections.splice(result.source.index, 1);
    newSections.splice(result.destination.index, 0, movedItem);
    setSections(newSections);

    const newIndex = calculateNewIndex(newSections, result.destination.index);

    // If indexes are too close, renumber all
    const minGap = getMinGap(newSections.map((s) => s.index));
    if (minGap < 1) {
      console.log("Reindexing all sections to restore spacing...");
      await renumberIndexes(newSections, documentId, setSections);
    } else {
      await updateDoc(
        doc(db, "documents", documentId, "sections", movedItem.id),
        { index: newIndex }
      );
    }
  };

  return (
    <Stack
      height="100%"
      sx={{
        overflowY: "auto",
      }}
      pb={4}
    >
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sections-list">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              {sections.map((section, index) => (
                <Draggable key={section.id} draggableId={section.id} index={index}>
                  {(provided) => (
                    <div
                      ref={(el) => {
                        provided.innerRef(el);
                        sectionRefs.current[section.id] = el;
                      }}
                      {...provided.draggableProps}
                    >
                      <Section
                        section={section}
                        dragHandleProps={provided.dragHandleProps}
                      />
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <ExpandableActionButtons
                          actions={[
                            <AddSectionButton
                              key="header"
                              documentId={documentId}
                              type={HEADER_TYPE}
                              previousSection={sections[index]}
                              nextSection={sections[index + 1]}
                            />,
                            <AddSectionButton
                              key="markdown"
                              documentId={documentId}
                              type={MARKDONW_TYPE}
                              previousSection={sections[index]}
                              nextSection={sections[index + 1]}
                            />,
                            <AddSectionButton
                              key="image"
                              documentId={documentId}
                              type={IMAGE_TYPE}
                              previousSection={sections[index]}
                              nextSection={sections[index + 1]}
                            />,
                            <AddSectionButton
                              key="ai"
                              documentId={documentId}
                              type={AI_TYPE}
                              previousSection={sections[index]}
                              nextSection={sections[index + 1]}
                            />,
                          ]}
                        />
                      </Stack>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Stack>
  );
}

/**
 * Calculates a safe new index for the moved section,
 * leaving spacing between elements.
 */
function calculateNewIndex(sections, destinationIndex) {
  if (sections.length === 1) {
    return INDEX_GAP;
  }

  if (destinationIndex === 0) {
    return sections[1].index / 2;
  }

  if (destinationIndex === sections.length - 1) {
    return sections[sections.length - 2].index + INDEX_GAP;
  }

  const prev = sections[destinationIndex - 1].index;
  const next = sections[destinationIndex + 1].index;
  return (prev + next) / 2;
}

/**
 * Computes the minimum gap between sorted index values.
 */
function getMinGap(indexes) {
  const sorted = [...indexes].sort((a, b) => a - b);
  let minGap = Infinity;
  for (let i = 1; i < sorted.length; i++) {
    minGap = Math.min(minGap, sorted[i] - sorted[i - 1]);
  }
  return minGap;
}

/**
 * Renumbers all sections to restore spacing (e.g., 1000, 2000, 3000, ...).
 */
async function renumberIndexes(sections, documentId, setSections) {
  const batch = writeBatch(db);
  const newSections = sections.map((s, i) => ({
    ...s,
    index: (i + 1) * INDEX_GAP,
  }));

  newSections.forEach((s) => {
    batch.update(doc(db, "documents", documentId, "sections", s.id), {
      index: s.index,
    });
  });

  await batch.commit();
  setSections(newSections);
}
